import { REACH_CLIENT_HEADER_VALUE } from './constants.js';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * Generic utility for making authenticated API requests
 * Handles common fetch logic like credentials, headers, and error handling
 * @param endpoint - Relative endpoint path (e.g., '/api/partner/automation-templates')
 * @param options - Fetch options (method, body, headers, etc.)
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/')
    ? endpoint
    : `/${endpoint}`;
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Include cookies for WorkOS session
    headers: {
      'Content-Type': 'application/json',
      'X-Reach-Client': REACH_CLIENT_HEADER_VALUE,
      ...options.headers,
    },
  });

  // Handle 401 Unauthorized - redirect to login since backend handles refresh automatically
  if (response.status === 401) {
    // Don't redirect if this is already an auth endpoint
    const isAuthEndpoint =
      url.includes('/api/auth/refresh') ||
      url.includes('/api/auth/session') ||
      url.includes('/api/auth/login') ||
      url.includes('/api/auth/logout');

    if (!isAuthEndpoint) {
      // Clear any existing auth state and redirect to login
      window.location.href = `${API_BASE_URL}/api/auth/logout`;
      throw new Error('Session expired. Please log in again.');
    }
  }

  if (!response.ok) {
    // Try to parse error response data
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = null;
    }

    // Extract error message from response
    const errorMessage =
      errorData?.message ||
      errorData?.error ||
      response.statusText ||
      'API request failed';

    throw new Error(errorMessage);
  }

  return response.json();
}
